'use client';

import { Controller, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { Box, Dialog, DialogContent, Tooltip } from '@mui/material';
import { QuestionMark } from '@mui/icons-material';

import { ButtonIcon } from '../buttonIcon/ButtonIcon';
import { isMobile } from '../../utils/userAgent/userAgent.util';

import type { TValueType } from '../../types/components/components.type';
import type { IFormFiledProps } from './formFiled.type';

/**
 * Form Field Controller
 *
 * Use only inside Form component
 */
export const FormFiled = ({
  Component,
  name,
  label,
  disabled = false,
  componentProps = {},
  tooltip = '',
  helperText,
  handleChangeCallback,
}: IFormFiledProps) => {
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext();

  const t = useTranslations();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <Box sx={{ display: 'flex', alignItems: 'felx-start' }}>
          <Component
            {...componentProps}
            disabled={disabled || isSubmitting}
            name={name}
            label={label}
            // As because Component don`t know context and for him value can be any
            value={value as TValueType}
            handleChange={({ key, value: newValue }) => {
              onChange({ target: { name: key, value: newValue as TValueType } });

              if (handleChangeCallback) {
                handleChangeCallback({ value: newValue as TValueType, key });
              }
            }}
            handleBlur={onBlur}
            errorMessage={error?.message ? t(error.message) : ''}
            helperText={helperText}
          />

          {tooltip && (
            <>
              <Tooltip title={tooltip}>
                <div>
                  <ButtonIcon
                    handleClick={() => {
                      if (isMobile()) {
                        setOpenTooltip(true);
                      }
                    }}
                    aria-haspopup
                    aria-label="tooltip"
                    size="small"
                    sx={{ ml: 1, border: '1px solid', color: 'grey.700', borderColor: 'grey.400' }}
                  >
                    <QuestionMark fontSize="small" />
                  </ButtonIcon>
                </div>
              </Tooltip>
              <Dialog
                open={openTooltip}
                onClose={() => {
                  setOpenTooltip(false);
                }}
              >
                <DialogContent>{tooltip}</DialogContent>
              </Dialog>
            </>
          )}
        </Box>
      )}
    />
  );
};
